import React, { useEffect } from 'react';
import { CheckCircle, Calendar, Clock, Mail, ArrowLeft } from 'lucide-react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import Layout from '../components/layout/Layout';

const TerminBestaetigung: React.FC = () => {
  useEffect(() => {
    document.title = "Termin erfolgreich gebucht | OetterliApps";
    
    // Google Analytics Event für Conversion Tracking
    if (typeof window !== 'undefined' && (window as any).gtag) {
      (window as any).gtag('event', 'conversion', {
        'send_to': 'AW-CONVERSION_ID/CONVERSION_LABEL', // Hier deine Google Ads Conversion ID einfügen
        'event_category': 'booking',
        'event_label': 'termin_gebucht',
        'value': 297 // Wert des kostenlosen Beratungsgesprächs
      });
    }
    
    // Facebook Pixel Event (falls verwendet)
    if (typeof window !== 'undefined' && (window as any).fbq) {
      (window as any).fbq('track', 'Lead', {
        value: 297,
        currency: 'CHF',
        content_name: 'Kostenlose Beratung'
      });
    }
  }, []);

  return (
    <Layout>
      <section className="min-h-screen bg-gradient-to-br from-green-50 via-white to-blue-50 py-20">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, type: "spring" }}
            className="max-w-2xl mx-auto text-center"
          >
            {/* Success Icon */}
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
              className="inline-flex items-center justify-center w-24 h-24 bg-gradient-to-br from-green-400 to-emerald-500 rounded-full mb-8 shadow-2xl"
            >
              <CheckCircle className="w-12 h-12 text-white" />
            </motion.div>

            {/* Main Headline */}
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3, duration: 0.6 }}
              className="text-4xl md:text-5xl font-bold text-gray-900 mb-6"
            >
              🎉 Perfekt! 
              <span className="block text-green-600 mt-2">
                Dein Termin ist gesichert!
              </span>
            </motion.h1>

            {/* Confirmation Message */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4, duration: 0.6 }}
              className="bg-white rounded-3xl shadow-xl p-8 mb-8"
            >
              <h2 className="text-2xl font-semibold text-gray-800 mb-4">
                Was passiert jetzt?
              </h2>
              
              <div className="space-y-6">
                <div className="flex items-start text-left">
                  <div className="flex-shrink-0 w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center mr-4">
                    <Mail className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-800">E-Mail Bestätigung</h3>
                    <p className="text-gray-600">Du erhältst in den nächsten Minuten eine Bestätigung per E-Mail mit allen Details.</p>
                  </div>
                </div>

                <div className="flex items-start text-left">
                  <div className="flex-shrink-0 w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center mr-4">
                    <Calendar className="w-5 h-5 text-purple-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-800">Persönlicher Anruf</h3>
                    <p className="text-gray-600">Ich melde mich innerhalb von 24 Stunden, um einen passenden Termin zu vereinbaren.</p>
                  </div>
                </div>

                <div className="flex items-start text-left">
                  <div className="flex-shrink-0 w-10 h-10 bg-green-100 rounded-full flex items-center justify-center mr-4">
                    <Clock className="w-5 h-5 text-green-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-800">30-Min Beratung</h3>
                    <p className="text-gray-600">Wir besprechen deine Herausforderungen und entwickeln konkrete Lösungsansätze.</p>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Value Proposition */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5, duration: 0.6 }}
              className="bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-3xl p-8 mb-8"
            >
              <h3 className="text-2xl font-bold mb-4">Dein kostenloser Quick-Check</h3>
              <div className="grid md:grid-cols-3 gap-4 text-center">
                <div>
                  <div className="text-3xl font-bold">297 CHF</div>
                  <div className="text-sm opacity-80">Beratungswert</div>
                </div>
                <div>
                  <div className="text-3xl font-bold">30 Min</div>
                  <div className="text-sm opacity-80">Persönliches Gespräch</div>
                </div>
                <div>
                  <div className="text-3xl font-bold">100%</div>
                  <div className="text-sm opacity-80">Kostenlos</div>
                </div>
              </div>
            </motion.div>

            {/* Next Steps */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6, duration: 0.6 }}
              className="text-gray-600 mb-8"
            >
              <p className="text-lg">
                Bereite dich schon mal vor! Denk an deine größten digitalen Herausforderungen – 
                gemeinsam finden wir die perfekte Lösung.
              </p>
            </motion.div>

            {/* Action Buttons */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.7, duration: 0.6 }}
              className="flex flex-col sm:flex-row gap-4 justify-center"
            >
              <Link
                to="/"
                className="inline-flex items-center justify-center px-6 py-3 bg-white text-gray-700 font-semibold rounded-full shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-200"
              >
                <ArrowLeft className="w-5 h-5 mr-2" />
                Zurück zur Startseite
              </Link>
              
              <a
                href="mailto:contact@oetterliapps.ch?subject=Vorbereitung%20Beratungsgespräch"
                className="inline-flex items-center justify-center px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white font-semibold rounded-full shadow-lg hover:shadow-xl transition-all duration-300"
              >
                <Mail className="w-5 h-5 mr-2" />
                Direkt E-Mail schreiben
              </a>
            </motion.div>

            {/* Social Proof */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.8, duration: 0.6 }}
              className="mt-12 text-center"
            >
              <p className="text-gray-500 text-sm">
                Du bist in bester Gesellschaft – über 50+ Schweizer Unternehmen vertrauen bereits auf OetterliApps
              </p>
            </motion.div>
          </motion.div>
        </div>
      </section>
    </Layout>
  );
};

export default TerminBestaetigung;